import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const icons = {
  info: <InformationCircleIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />,
  success: <CheckCircleIcon className="h-6 w-6 text-green-400" aria-hidden="true" />,
  warning: <ExclamationCircleIcon className="h-6 w-6 text-yellow-400" aria-hidden="true" />,
  error: <XCircleIcon className="h-6 w-6 text-red-400" aria-hidden="true" />,
};

export const Notification = ({ notification: { id, type, title, description, message }, onDismiss }) => {
  const [show, setShow] = useState(true);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    if (!hovering) {
      const timer = setTimeout(() => setShow(false), 4500);
      return () => clearTimeout(timer);
    }
  }, [hovering]);

  useEffect(() => {
    if (!show) {
      const timeout = setTimeout(() => onDismiss(id), 300);
      return () => clearTimeout(timeout);
    }
  }, [show, id, onDismiss]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.3 }}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          role="alert"
          aria-label={title}
          className="relative w-full max-w-sm bg-[linear-gradient(to_bottom,_#2F6ACB,_#4455D0,_#4B3CBF)] dark:bg-[linear-gradient(to_bottom,_#0F2027,_#203A43,_#2C5364)] backdrop-blur-md border border-white/20 dark:border-white/10 shadow-xl rounded-lg overflow-hidden text-white pointer-events-auto"
        >
          <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-cyan-400 to-transparent animate-progress" />
          <div className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 mt-1">{icons[type]}</div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-semibold mb-1">{title}</p>
                <p className="text-sm text-white/80 leading-snug">
                  {description || message}
                </p>
              </div>
              <button
                onClick={() => setShow(false)}
                className="ml-4 text-white/60 hover:text-white"
              >
                <XIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <style jsx>{`
            @keyframes progress {
              0% {
                width: 100%;
              }
              100% {
                width: 0%;
              }
            }
            .animate-progress {
              animation: progress 4.5s linear forwards;
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
